import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import {
  getExecutionBrief,
  resetExecutionBrief,
  updateExecutionBrief,
} from "../../../../redux/actions/executionBriefListActions";
import { getReviewStages, resetReviewStages } from "../../../../redux/actions/reviewStagesActions";
import { errorMessage } from "../../../../redux/actions/messagesActions";
import {
  selectBreadcrumbInfo,
  selectCampaignId,
  selectExecBriefOwnerName,
  selectExecBriefUpdatedTime,
  selectExecutionBrief,
  selectExecutionBriefFormErrors,
  selectExecutionBriefId,
  selectExecutionBriefLoader,
  selectExecutionBriefMerchOwners,
  selectExecutionBriefNdaData,
  selectExecutionBriefOwnerId,
  selectExecutionBriefsErrors,
  selectExecutionBriefStatus,
  selectExecutionName,
  selectExecutionResourceType,
  selectHierarchyDetails,
  selectReviewers,
} from "../../../../redux/selectors/executionBriefs";
import { BriefType } from "../../../../constants/types";
import { errorMessageForDatesValidation } from "../../../../utils/validationUtils";
import { setPermissions } from "../../../../redux/actions/permissionsActions";
import { selectPageOwnerId, selectPageStatus } from "../../../../redux/selectors/root";
import { selectWorkOrderOwnerId } from "../../../../redux/selectors/workOrders";
import { TPageType } from "../../../../types/common";
import { BriefWrapper } from "../../../../components/brief/brief-wrapper/brief-wrapper.component";
import { DuplicateExecutionBriefModal } from "../../../../components/modals/duplicate-execution-brief-modal/components/duplicate-execution-brief-modal/duplicate-execution-brief-modal.component";
import * as userStore from "../../../../features/user/store/user.store";
import { calculatePermissions } from "../../../../types/user/permissions/ipermissions/ipermissions.functions";
import { IExtraAction } from "../../../../features/brief-header/types/iextra-action.interface";
import { BriefLoader } from "../../../../components/brief/BriefLoader";

import { getExecutionBriefDefaultValues } from "../../types/execution-brief-form.functions";
import * as executionBriefStore from "../../store/execution-brief.store";
import { getExecutionBriefTemplateValidationSchema } from "../../types/execution-brief-template.functions";

import { ExecutionBriefForm } from "../execution-brief-form/execution-brief-form.component";
import { FileDownload } from "../../../../features/file-attachments/file-download/components/file-download/file-download-component";
import { exportBriefEndpoint } from "../../services/execution-brief.service";

export const ExecutionBrief = () => {
  const pageType: TPageType = BriefType.EXECUTION_BRIEF;

  const dispatch = useDispatch();

  const brief = useSelector(selectExecutionBrief);

  const campaignBriefId = useSelector(selectCampaignId);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState<boolean>(false);
  const { id } = useParams<{ id: string }>();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const breadcrumbInfo = useSelector(selectBreadcrumbInfo);
  const briefId = useSelector(selectExecutionBriefId);
  const briefOwner = useSelector(selectExecBriefOwnerName);
  const errors = useSelector(selectExecutionBriefsErrors);
  const formErrors = useSelector(selectExecutionBriefFormErrors);
  const executionName = useSelector(selectExecutionName);

  const executionTemplate = useSelector(selectExecutionResourceType);
  const isLoading = useSelector(selectExecutionBriefLoader);
  const ndaData = useSelector(selectExecutionBriefNdaData);
  const status = useSelector(selectExecutionBriefStatus);

  const updatedTime = useSelector(selectExecBriefUpdatedTime);

  const validationSchema = getExecutionBriefTemplateValidationSchema(executionTemplate);

  const user = useSelector(userStore.getUser);
  const userId = useSelector(userStore.getUserId);
  const userGroups = useSelector(userStore.getUserGroup);

  const executionOwnerId = useSelector(selectExecutionBriefOwnerId);
  const workorderOwnerId = useSelector(selectWorkOrderOwnerId);
  const pageStatus = useSelector(selectPageStatus(pageType));
  const pageOwnerId = useSelector(selectPageOwnerId(pageType));
  const reviewers = useSelector(selectReviewers);
  const hierarchyDetails = useSelector(selectHierarchyDetails);
  const merchOwners = useSelector(selectExecutionBriefMerchOwners);
  const isContentCreator =
    userId && hierarchyDetails && hierarchyDetails.workOrderContentCreators.includes(userId);
  const parentReviewers = hierarchyDetails?.campaignBriefReviewers;
  const briefOwnerId = hierarchyDetails.campaignBriefOwnerId;

  const [permissionsCreated, setPermissionsCreated] = useState<boolean>(false);

  const defaultValues = useMemo(() => getExecutionBriefDefaultValues(brief), [brief]);

  useEffect(() => {
    setDuplicateModalOpen(false);
  }, [id]);

  useEffect(() => {
    return () => {
      dispatch(resetExecutionBrief());
      dispatch(resetReviewStages());
    };
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      // get information about a specific execution brief
      dispatch(getExecutionBrief(id));
      dispatch(getReviewStages(Number(id), pageType));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setPermissionsCreated(false);
  }, [pageStatus]);

  useEffect(() => {
    if (briefId) {
      const permissionContext = {
        userId,
        userGroups,
        pageType,
        briefOwnerId,
        executionOwnerId,
        workorderOwnerId,
        pageStatus,
        pageOwnerId,
        reviewers,
        parentReviewers,
        merchOwners,
        contentCreators: isContentCreator ? [user] : [],
      };

      const newPermissions = calculatePermissions(permissionContext);
      setPermissionsCreated(true);
      dispatch(setPermissions(newPermissions));
    }
  }, [
    briefId,
    dispatch,
    permissionsCreated,
    user,
    userId,
    userGroups,
    pageType,
    briefOwnerId,
    executionOwnerId,
    workorderOwnerId,
    pageStatus,
    pageOwnerId,
    reviewers,
    parentReviewers,
    merchOwners,
    isContentCreator,
  ]);

  const extraActions = (): IExtraAction[] => {
    return [
      {
        action: () => setDuplicateModalOpen(true),
        label: "Duplicate",
        show: !ndaData.isUnderNda,
      },
      {
        label: (
          <FileDownload
            url={exportBriefEndpoint(briefId)}
            authRequired
            saveAsFileName="execution-brief-export.docx"
          >
            Export
          </FileDownload>
        ),
      },
    ];
  };

  const onSubmit = (data: any) => {
    const datesValidationError = errorMessageForDatesValidation(
      data.startAt,
      data.endAt,
      status,
      data.status
    );
    if (datesValidationError !== null) {
      dispatch(errorMessage(datesValidationError));
    } else {
      setIsSaving(true);
      dispatch(
        updateExecutionBrief({
          executionName,
          executionTemplate,
          id: briefId,
          campaignBrief: campaignBriefId,
          ...data,
        })
      );
    }
  };

  return (
    <>
      <BriefLoader showIf={!Boolean(briefId)} />
      <BriefWrapper
        showIf={Boolean(briefId)}
        breadcrumbInfo={breadcrumbInfo}
        briefId={briefId}
        briefType={BriefType.EXECUTION_BRIEF}
        ContentComponent={ExecutionBriefForm}
        defaultValues={defaultValues}
        extraActions={extraActions()}
        formErrors={formErrors}
        isLoading={isLoading}
        isSaving={isSaving}
        name={executionName}
        ndaData={ndaData}
        onSubmit={onSubmit}
        owner={briefOwner}
        setIsSaving={setIsSaving}
        stateErrors={errors}
        status={status}
        template={executionTemplate}
        updatedTime={updatedTime}
        validationSchema={validationSchema}
      />
      <DuplicateExecutionBriefModal
        errors={errors}
        id={briefId}
        isOpen={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        onSubmit={executionBriefStore.duplicate}
      />
    </>
  );
};
